import {
  applyFixedMeetingsOrganizerScope,
  buildSupergraphFilterFromUiFilters,
  DEFAULT_SUPERGRAPH_MEETING_KINDS,
} from 'features/meetings/hooks/search_data/supergraph_meeting_search_utils'
import { INVALID_SUPERGRAPH_SCOPE_TYPES } from 'features/meetings/supergraph/scope_utils'
import { mapScopeTypeToBackendValue } from 'shared/api/meetings/utils'

const MEETINGS_PAGE_LIST_FILTER_WHITELIST = new Set([
  'attendee_emails',
  'cancelled',
  'end_time',
  'event_type_uuids',
  'has_mi',
  'ids',
  'include_notetaker_ineligible_external_events',
  'invitee_emails',
  'is_organizer',
  'kinds',
  'location_kind',
  'meeting_intelligence_status',
  'owner_type',
  'package_uuids',
  'role',
  'scope_id',
  'scope_type',
  'search_keyword',
  'start_time',
  'status',
  'statuses',
  'team_uuids',
  'tracking_ids',
  'user_uuids',
  'utm_campaign',
  'utm_content',
  'utm_medium',
  'utm_source',
  'utm_term',
  'uuid',
])

const pickMeetingsPageListFilter = (raw) => {
  if (!raw || typeof raw !== 'object') return {}
  const out = {}
  for (const key of Object.keys(raw)) {
    if (MEETINGS_PAGE_LIST_FILTER_WHITELIST.has(key)) {
      out[key] = raw[key]
    }
  }
  return out
}

/**
 * Calendar list text search: same time slicing as cmd `buildSupergraphInput` but targets supergraph
 * `meetingsPageList` (`MeetingsSearch_MeetingsInput`). `search_keyword` is a **sibling** of `filter`;
 * `supergraphql_api` merges it into `filter.search_keyword` on the wire.
 */
export const buildMeetingsSearchSupergraphCalendarSliceInput = (uiFilters, {
  today, past, first, after, order, fromStartOfToday,
}) => {
  if (!uiFilters) return null
  const filter = buildSupergraphFilterFromUiFilters(uiFilters, { today, past, fromStartOfToday })
  if (!filter) return null
  const { search_keyword: kwFromFilter, ...rest } = filter
  let searchKeywordSibling = ''
  if (kwFromFilter != null && kwFromFilter !== '') {
    searchKeywordSibling = String(kwFromFilter)
  } else if (typeof uiFilters.searchKeyword === 'string') {
    searchKeywordSibling = uiFilters.searchKeyword.trim()
  }
  const pickedFilter = pickMeetingsPageListFilter(rest)
  return {
    ...(searchKeywordSibling ? { search_keyword: searchKeywordSibling } : {}),
    filter: pickedFilter,
    pagination: { first, after: after || null },
    orderBy: { start_time: order },
  }
}

/**
 * Builds search filter-modal input for supergraph `meetingsPageList` (counts) and related search paths.
 * `search_keyword` is a **sibling** of `filter`; `supergraphql_api` merges it into `filter.search_keyword`
 * on the wire.
 */
export const buildMeetingsSearchSupergraphInput = ({
  searchKeyword,
  preparedFilter,
  scopeType,
  scopeId,
}) => {
  const filter = {}

  const q = typeof searchKeyword === 'string' ? searchKeyword.trim() : ''

  if (preparedFilter?.inviteeEmails?.length) {
    filter.invitee_emails = preparedFilter.inviteeEmails
  }
  if (preparedFilter?.eventTypeUuids?.length) {
    filter.event_type_uuids = preparedFilter.eventTypeUuids
  }
  if (preparedFilter?.packageUuids?.length) {
    filter.package_uuids = preparedFilter.packageUuids
  }
  if (preparedFilter?.kinds?.length) {
    filter.kinds = preparedFilter.kinds
  } else {
    filter.kinds = [...DEFAULT_SUPERGRAPH_MEETING_KINDS]
  }
  if (preparedFilter?.role?.length) {
    filter.role = preparedFilter.role
  }

  if (preparedFilter?.statuses?.length === 1) {
    filter.status = preparedFilter.statuses[0]
  } else if (preparedFilter?.statuses?.length > 1) {
    filter.statuses = preparedFilter.statuses
  }

  if (preparedFilter?.startTimeRange) {
    const { operation, start, end } = preparedFilter.startTimeRange
    filter.start_time = { operation, start, end }
  }

  if (preparedFilter?.userIds?.length) {
    filter.user_uuids = preparedFilter.userIds
  }
  if (preparedFilter?.teamIds?.length) {
    filter.team_uuids = preparedFilter.teamIds
  }

  const mappedScope = mapScopeTypeToBackendValue(scopeType, scopeId)

  if (mappedScope === 'all_users_and_teams') {
    if (scopeId) filter.scope_id = scopeId
  } else if (mappedScope && !INVALID_SUPERGRAPH_SCOPE_TYPES.has(mappedScope)) {
    filter.scope_type = mappedScope
    if (scopeId && mappedScope !== 'my_calendly' && scopeId !== 'me') {
      filter.scope_id = scopeId
    }
  }

  applyFixedMeetingsOrganizerScope(filter)

  const pickedFilter = pickMeetingsPageListFilter(filter)

  return {
    ...(q ? { search_keyword: q } : {}),
    filter: pickedFilter,
    pagination: { first: 1, after: null },
    orderBy: { start_time: 'DESC' },
  }
}
